/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useLayout } from "@/../_metronic/layout/core";
import {
	ResumeCitizenGroupActivitiesIndicatorB4Data,
	getResumeCitizensGroupActivitiesIndicatorB4,
} from "@/services/esus/indicator/esb/b4/getResumeCitizensGroupActivitiesIndicatorB4";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import {
	getResumeIndicatorB4,
	ResumeIndicatorB4Data,
} from "@/services/esus/indicator/esb/b4/getResumeIndicatorB4";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getResumeIndicatorB4ByHealthTeam,
	ResumeIndicatorB4ByHealthTeamData,
} from "@/services/esus/indicator/esb/b4/getResumeIndicatorB4ByHealthTeam";

import { IndicatorEsbB4Resume } from "./IndicatorEsbB4Resume";
import { IndicatorEsbB4GroupActivities } from "./IndicatorEsbB4GroupActivity";
import { IndicatorEsbB4HealthTeams } from "./IndicatorEsbB4HealthTeams";

type IndicatorEsbB4Props = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	isPendingToUpdate: boolean;
	setIsPendingToUpdate: (isPendingToUpdate: boolean) => void;
};

export function IndicatorEsbB4({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	isPendingToUpdate,
	setIsPendingToUpdate,
}: Readonly<IndicatorEsbB4Props>) {
	const { setTitle } = useLayout();
	const { uf, ibgeCode } = useCurrentAccount();
	const pageSize = 10;
	const [resumeIndicatorB4, setResumeIndicatorB4] = useState<ResumeIndicatorB4Data>(
		new ResumeIndicatorB4Data()
	);

	const [pageNumberGroupActivities, setPageNumberGroupActivities] = useState(1);
	const [searchGroupActivities, setSearchGroupActivities] = useState("");
	const debouncedSearchGroupActivities = useDebounce(searchGroupActivities, 2000);
	const [isEvaluatedGroupActivities, setIsEvaluatedGroupActivities] = useState<
		boolean | undefined
	>(true);
	const [paginatedGroupActivities, setPaginatedGroupActivities] = useState(
		new Paginated<ResumeCitizenGroupActivitiesIndicatorB4Data>()
	);
	const [isLoadingGroupActivities, setIsLoadingGroupActivities] = useState(false);

	const [pageNumberHealthTeamsClassifications, setPageNumberHealthTeamsClassifications] =
		useState(1);
	const [searchHealthTeamsClassification, setSearchHealthTeamsClassification] = useState("");
	const debouncedSearchHealthTeamsClassifications = useDebounce(
		searchHealthTeamsClassification,
		2000
	);
	const [paginatedHealthTeamsClassifications, setPaginatedHealthTeamsClassifications] = useState(
		new Paginated<ResumeIndicatorB4ByHealthTeamData>()
	);
	const [isLoadingHealthTeamsClassifications, setIsLoadingHealthTeamsClassifications] =
		useState(false);

	async function handleFilter() {
		await Promise.all([
			handleGetResumeIndicatorB4(),
			handleGetResumeIndicatorB4ByHealthTeam(),
			handleGetResumeCitizensGroupActivitiesIndicatorB4(),
		]);
		setIsPendingToUpdate(false);
	}

	async function handleGetResumeIndicatorB4() {
		setResumeIndicatorB4(
			await getResumeIndicatorB4({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
			})
		);
	}

	async function handleGetResumeIndicatorB4ByHealthTeam(pageNumber?: number) {
		setIsLoadingHealthTeamsClassifications(true);
		setPaginatedHealthTeamsClassifications(
			await getResumeIndicatorB4ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingHealthTeamsClassifications(false);
	}

	async function handleGetAllResumeIndicatorB4ByHealthTeam() {
		return (
			await getResumeIndicatorB4ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: 1,
				pageSize: paginatedHealthTeamsClassifications.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetResumeCitizensGroupActivitiesIndicatorB4(pageNumber?: number) {
		setIsLoadingGroupActivities(true);
		setPaginatedGroupActivities(
			await getResumeCitizensGroupActivitiesIndicatorB4({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchGroupActivities,
				isEvaluated: isEvaluatedGroupActivities,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingGroupActivities(false);
	}

	async function handleGetAllResumeCitizensGroupActivitiesIndicatorB4() {
		return (
			await getResumeCitizensGroupActivitiesIndicatorB4({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchGroupActivities,
				isEvaluated: isEvaluatedGroupActivities,
				pageNumber: 1,
				pageSize: paginatedGroupActivities.totalRecords,
				export: true,
			})
		).data;
	}

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeCitizensGroupActivitiesIndicatorB4(pageNumberGroupActivities);
	}, [pageNumberGroupActivities]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberGroupActivities === 1) {
				handleGetResumeCitizensGroupActivitiesIndicatorB4(pageNumberGroupActivities);
			} else {
				setPageNumberGroupActivities(1);
			}
		}
	}, [debouncedSearchGroupActivities, isEvaluatedGroupActivities]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeIndicatorB4ByHealthTeam(pageNumberHealthTeamsClassifications);
	}, [pageNumberHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberHealthTeamsClassifications === 1) {
				handleGetResumeIndicatorB4ByHealthTeam(pageNumberHealthTeamsClassifications);
			} else {
				setPageNumberHealthTeamsClassifications(1);
			}
		}
	}, [debouncedSearchHealthTeamsClassifications]);

	useEffect(() => {
		if (isPendingToUpdate) handleFilter();
	}, [isPendingToUpdate]);

	useEffect(() => {
		setTitle("B4 - Escovação Supervisionada em Faixa Etária Escolar de 6 a 12 anos");
	}, [setTitle]);

	return (
		<>
			<IndicatorEsbB4Resume resume={resumeIndicatorB4} />
			<IndicatorEsbB4HealthTeams
				paginatedHealthTeamsClassifications={paginatedHealthTeamsClassifications}
				isLoading={isLoadingHealthTeamsClassifications}
				search={searchHealthTeamsClassification}
				setSearch={setSearchHealthTeamsClassification}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberHealthTeamsClassifications}
				setPageNumber={setPageNumberHealthTeamsClassifications}
				handleGetAllResumeIndicatorB4ByHealthTeam={
					handleGetAllResumeIndicatorB4ByHealthTeam
				}
			/>
			<IndicatorEsbB4GroupActivities
				paginatedGroupActivities={paginatedGroupActivities}
				isLoading={isLoadingGroupActivities}
				search={searchGroupActivities}
				setSearch={setSearchGroupActivities}
				isEvaluated={isEvaluatedGroupActivities}
				setIsEvaluated={setIsEvaluatedGroupActivities}
				selectedQuarter={selectedQuarter}
				selectedYear={selectedYear}
				selectedMonth={selectedMonth}
				selectedIne={selectedIne}
				pageNumber={pageNumberGroupActivities}
				setPageNumber={setPageNumberGroupActivities}
				handleGetAllResumeCitizensGroupActivitiesIndicatorB4={
					handleGetAllResumeCitizensGroupActivitiesIndicatorB4
				}
			/>
		</>
	);
}
